import type { BodyPose } from './bodyPoseFrame.ts';
import { SKY_RACE_JOINTS, type SkyRaceMode, type SkyRacePose } from './skyRaceFrame.ts';

/**
 * Sky Race на headless-ТВ (GEN-18912): поза для страницы берётся из общего кадра `BodyPoseData`, уровень и режим
 * выбираются кнопками на ТВ из `GET /api/public/v1/games/sky-race/levels`.
 */

/** Четыре точки управления самолётом; без любой из них — `null` (страница сама выровняет самолёт по таймауту). */
export function skyRacePoseFromBody(pose: BodyPose | null): SkyRacePose | null {
  if (!pose) return null;
  const result: Partial<SkyRacePose> = {};
  const complete = SKY_RACE_JOINTS.every((joint) => {
    const point = pose[joint];
    if (point) result[joint] = { x: point.x, y: point.y };
    return point !== undefined;
  });
  return complete ? (result as SkyRacePose) : null;
}

export interface SkyRaceLevelOption {
  slug: string;
  name: string;
  modes: SkyRaceMode[];
}

const ALL_MODES: readonly SkyRaceMode[] = ['timed', 'standard'];

/** Встроенный уровень страницы — если список не загрузился или пуст. */
export function defaultSkyRaceLevels(name: string): SkyRaceLevelOption[] {
  return [{ slug: 'valley', name, modes: [...ALL_MODES] }];
}

/** Ответ `GET /api/public/v1/games/sky-race/levels` → уровни для меню; битые записи пропускаются, пустой итог — встроенный уровень. */
export function parseSkyRaceLevels(raw: unknown, fallbackName: string): SkyRaceLevelOption[] {
  if (!Array.isArray(raw)) return defaultSkyRaceLevels(fallbackName);
  const levels = raw.flatMap((item): SkyRaceLevelOption[] => {
    if (!item || typeof item !== 'object') return [];
    const { slug, level_name: levelName, modes } = item as Record<string, unknown>;
    if (typeof slug !== 'string' || !slug) return [];
    const known = Array.isArray(modes) ? ALL_MODES.filter((m) => modes.includes(m)) : [];
    return [{
      slug,
      name: typeof levelName === 'string' && levelName ? levelName : slug,
      modes: known.length ? known : [...ALL_MODES],
    }];
  });
  return levels.length ? levels : defaultSkyRaceLevels(fallbackName);
}
